<template>
	<div :class="['alternative-flight', { 'alternative-flight--loading': loading }]">
		<Loading
			v-if="loading"
			size="medium"
			class="alternative-flight__loader"
		/>
		<FlightContainer
			:class="['alternative-flight__flight-container', containerClasses]"
			:vertical-divider="false"
			:with-flight-details="verified && !notAvailable"
			@show-flight-details="showModal"
		>
			<template #outbound>
				<FlightSegment
					v-if="verified && verifiedOffer"
					:flight-segments="verifiedOffer.OutboundFlightSegments"
				/>
				<UnverifiedFlightDetails
					v-else
					:flight-data="alternativeFlight.OutboundFlight"
				/>
			</template>
			<template #inbound>
				<FlightSegment
					v-if="verified && verifiedOffer"
					:flight-segments="verifiedOffer.InboundFlightSegments"
				/>
				<UnverifiedFlightDetails
					v-else
					:flight-data="alternativeFlight.InboundFlight"
				/>
			</template>
			<template #price>
				<PriceRow
					v-if="hasDifference"
					:price-props="{
						price: Math.abs(priceDifference.difference),
						currency: priceDifference.currency,
						showDecimals: true,
						prefix: pricePrefix,
						theme: priceColor,
					}"
					class="alternative-flight__extra-price"
					font-size="small"
					:text="priceText"
				/>
				<PriceRow
					v-if="!notAvailable && offerPrice.amount"
					class="alternative-flight__price"
					:price-props="{
						price: verifiedPrice ?? offerPrice.amount,
						currency: offerPrice.currency,
						showDecimals: !!verifiedPrice,
						theme: 'inherit',
					}"
					:font-size="hasDifference ? 'medium' : 'large'"
					:text="translate('pricing.total')"
				/>
			</template>
			<template #button>
				<template v-if="verified">
					<RoundedButton
						v-if="notAvailable"
						class="alternative-flight__button"
						:disabled="true"
					>
						Nicht mehr verfügbar
					</RoundedButton>
					<RoundedButton
						v-else
						class="alternative-flight__button"
						theme="extra"
						as="a"
						:href="bookingUrl"
						rel="nofollow"
						@click="fireAnalytics"
					>
						{{ translate('offer.addToCart') }}
					</RoundedButton>
				</template>
				<RoundedButton
					v-else
					class="alternative-flight__button"
					:disabled="loading"
					@click="verifyAlternativeFlight"
				>
					{{ translate('offer.verifyOffer') }}
				</RoundedButton>
			</template>
		</FlightContainer>
		<OfferDetailsModal
			ref="flightDetailsModal"
			class="alternative-flight__flight-details-modal"
			:title="translate('offer.flight.details')"
		>
			<div v-if="verifiedOffer?.InboundFlightSegments && verifiedOffer?.OutboundFlightSegments">
				<CheckoutFlightDetails
					:inbound-flight="verifiedOffer.InboundFlightSegments"
					:outbound-flight="verifiedOffer.OutboundFlightSegments"
					:has-stopover="
						verified && !notAvailable
							? !!(verifiedOffer?.InboundFlightSegments.length > 1 || verifiedOffer.OutboundFlightSegments.length > 1)
							: ''
					"
				/>
			</div>
		</OfferDetailsModal>
	</div>
</template>

<script setup lang="ts">
import type { AlternativeFlightsData, MergedOfferItemData } from '@/interfaces/offer';
import FlightContainer from './FlightContainer.vue';
import FlightSegment from './FlightSegment.vue';
import type { SearchFormDataType } from '@/interfaces/search-form';
import { computed, type Ref, ref, useTemplateRef } from 'vue';
import UnverifiedFlightDetails from './UnverifiedFlightDetails.vue';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import { getParam } from '@/components/common/services/url';
import { verifyOffer } from '@/components/common/services/verifyOfferService';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import PriceRow from './PriceRow.vue';
import OfferDetailsModal from '@lmt-rpb/OfferDetailsModal/OfferDetailsModal.vue';
import CheckoutFlightDetails from '@lmt-rpb/CheckoutFlightDetails/CheckoutFlightDetails.vue';

import { getPricing } from '@/js/utils/priceUtils';
import { useOfferStatus } from '@helper/composables/offerStatus';
import { useTranslations } from '@helper/composables/translations';
import type { ClientCurrency } from '@global-js/constants';
import { getBookingUrl } from '@/js/utils/urlUtils';
type Props = {
	alternativeFlight: AlternativeFlightsData;
	price: { amount: number | null; currency: ClientCurrency };
	formData: SearchFormDataType;
};

const { formData, alternativeFlight, price } = defineProps<Props>();

const verified = ref(false);
const notAvailable = ref(false);
const loading = ref(false);
const REQUEST_CANCEL_TOKEN = 'itemDetails';
const isDebuggingAirportsMissing = () => getParam('debuggingMissingAirports') === '1';
const verifiedOffer: Ref<null | MergedOfferItemData> = ref(null);
const status = computed(() => verifiedOffer.value?.Status);
const { translate } = useTranslations();

const { isOfferAvailable, isOfferOnRequest, offerCanBeChecked } = useOfferStatus(status);

const containerClasses = computed(() => ({
	'alternative-flight__flight-container--loading': loading.value,
	...(verified.value && {
		'alternative-flight__flight-container--available': isOfferAvailable.value,
		'alternative-flight__flight-container--on-request': isOfferOnRequest.value,
		'alternative-flight__flight-container--check-unable': offerCanBeChecked.value,
		'alternative-flight__flight-container--not-available': notAvailable.value,
	}),
}));

const offerPrice = computed(() => getPricing(alternativeFlight.Pricing));
const verifiedPrice = computed(() => {
	if (verifiedOffer.value === undefined || verifiedOffer.value === null) {
		return null;
	}
	if (verifiedOffer.value.Status === 'CheckUnable' || verifiedOffer.value.Status === 'Undefined') {
		return offerPrice.value.amount;
	}
	return typeof verifiedOffer.value.Price === 'string' ? Number.parseFloat(verifiedOffer.value.Price) : verifiedOffer.value.Price;
});

const priceDifference = computed(() => {
	const { amount: currentPrice, currency } = price;
	if (verifiedPrice.value === null || currentPrice === null) {
		return {
			difference: 0,
			currency,
		};
	}
	return {
		difference: verifiedPrice.value - currentPrice,
		currency,
	};
});

const hasDifference = computed(() => {
	return verified.value && !notAvailable.value && priceDifference.value.difference !== 0;
});
const priceText = computed(() => (priceDifference.value.difference > 0 ? translate('pricing.surcharge') : translate('pricing.discount')));
const priceColor = computed(() => (priceDifference.value.difference > 0 ? 'accent' : 'primary'));
const pricePrefix = computed(() => (priceDifference.value.difference > 0 ? '+' : '-'));

const bookingUrl = computed(() => getBookingUrl(verifiedOffer.value, alternativeFlight));

const flightDetailsModal = useTemplateRef('flightDetailsModal');
const showModal = (): void => {
	flightDetailsModal.value?.open();
};

// TODO: Google Analytics
const fireAnalytics = () => {};

const verifyAlternativeFlight = async (): Promise<MergedOfferItemData | null> => {
	// TODO: Google Analytics
	loading.value = true;
	const itemData = {
		type: 'package',
		available: null,
		loading: false,
		duration: 0,
		Price: alternativeFlight.Price && alternativeFlight.Price.Amount ? alternativeFlight.Price.Amount : 0,
		CurrencyCode: alternativeFlight.Price.Currency,
		StartDate: alternativeFlight.OutboundFlight ? alternativeFlight.OutboundFlight.Departure.Date : '',
		EndDate: alternativeFlight.InboundFlight.Departure.Date,
		OfferID: alternativeFlight.OfferID,
	} as MergedOfferItemData;

	const cancelTokenName = `${REQUEST_CANCEL_TOKEN}-${itemData.OfferID}`;
	try {
		const results = (await verifyOffer(isDebuggingAirportsMissing(), formData, itemData, cancelTokenName)) as MergedOfferItemData;
		loading.value = false;

		if (!results?.available) {
			verified.value = true;
			notAvailable.value = true;
			return results;
		}
		if (results?.available) {
			verifiedOffer.value = results;
			verified.value = true;
			return results;
		}
	} catch (error) {
		console.error('vacancyCheck error', error);
	} finally {
		loading.value = false;
	}
	return null;
};
</script>

<style lang="scss" scoped>
/* stylelint-disable declaration-property-value-no-unknown */
.alternative-flight {
	position: relative;

	&:not(:last-child) {
		margin-bottom: 1.6rem;
	}

	&__flight-container {
		border: solid 0.1rem $color-inactive;
		padding: 1.6rem;
		border-radius: $border-radius-large;
		box-shadow: 0.3rem 0.4rem 1.4rem 0 $color-box-shadow;

		&--available {
			border: 0.2rem solid $color-lime-light;
		}

		&--on-request {
			border: 0.2rem solid $color-warning;
		}

		&--check-unable {
			border: 0.2rem solid $color-border;
		}

		&--not-available {
			opacity: 0.5;
		}

		&--loading {
			opacity: 0.15;
		}

		:deep(.flight-container__flight-details) {
			margin-top: 0.8rem;
		}

		:deep(.flight-container__bottom) {
			margin-top: 0.8rem;
		}
	}

	&__price {
		margin-bottom: 1.6rem;
	}

	&__button.button--extra {
		font-size: $font-small-2;
	}

	&__button.button {
		font-size: $font-small-2;
	}

	&__loader {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate3d(-50%, -50%, 0);
		z-index: 1;
	}
}
</style>
